import React from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { useDefaultProps } from './useDefaultProps';
import {
  ProviderDefaultPropSupportedComponentName,
  ProviderDefaultPropSupportedComponents,
} from './types';

export function withDefaultProps<
  K extends ProviderDefaultPropSupportedComponentName,
  C extends React.ComponentType<ProviderDefaultPropSupportedComponents[K]>,
>(Wrapped: C, componentKey: K) {
  const Component = Wrapped as React.ComponentType<any>;
  const WithDefaultProps = React.forwardRef<
    React.ElementRef<C>,
    React.ComponentProps<C>
  >((props, ref) => {
    const resolvedProps = useDefaultProps(
      componentKey,
      props as ProviderDefaultPropSupportedComponents[K],
    );
    return <Component ref={ref} {...resolvedProps} />;
  });

  WithDefaultProps.displayName =
    Wrapped.displayName || Wrapped.name || componentKey;

  // Copy static members (e.g. compound subcomponents like SidePanel.Header)
  // and preserve them in the return type so `SidePanel.Header` stays typed.
  return hoistNonReactStatics(
    WithDefaultProps,
    Wrapped,
  ) as typeof WithDefaultProps & hoistNonReactStatics.NonReactStatics<C>;
}
